// reports/ReportFilterBar.tsx
import React from "react";
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import Select, { type StylesConfig } from "react-select";
import { Plane, Ship, Play, RotateCcw } from "lucide-react";
import type { ReportFilters, SelectOption, ShipmentMode } from "./Types";
import { getDateTypeOptions } from "./Reportconfig";

interface Props {
  mode: ShipmentMode;
  onModeChange: (mode: ShipmentMode) => void; // in-page swap (no navigation)
  filters: ReportFilters;
  setFilter: <K extends keyof ReportFilters>(key: K, value: ReportFilters[K]) => void;
  onGenerate: () => void;
  onReset: () => void;
  canGenerate: boolean;
  dateError: string | null;
  loading: boolean;
  customerOptions: SelectOption[];
  carrierOptions: SelectOption[];
}

/* --- CD Track brand accent (tweak these two if the theme shade changes) --- */
const ACCENT = "#F5A524";
const ACCENT_DARK = "#DB9115";
const ACCENT_SOFT = "rgba(245, 165, 36, 0.12)";
const BORDER = "rgba(0, 0, 0, 0.23)";

/* Small caption label used above every field for a consistent look */
const FieldLabel: React.FC<{ children: React.ReactNode; required?: boolean }> = ({
  children,
  required,
}) => (
  <Typography
    variant="caption"
    sx={{ display: "block", mb: 0.5, fontWeight: 600, color: "text.secondary" }}
  >
    {children}
    {required && <Box component="span" sx={{ color: ACCENT, ml: 0.25 }}>*</Box>}
  </Typography>
);

/* react-select styled to match a 40px MUI "small" outlined field + amber focus */
const selectStyles: StylesConfig<SelectOption, true> = {
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    borderRadius: 8,
    fontSize: "0.875rem",
    backgroundColor: "#fff",
    borderColor: state.isFocused ? ACCENT : BORDER,
    boxShadow: state.isFocused ? `0 0 0 1px ${ACCENT}` : "none",
    "&:hover": { borderColor: state.isFocused ? ACCENT : "rgba(0,0,0,0.5)" },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  placeholder: (base) => ({ ...base, color: "rgba(0,0,0,0.5)", fontSize: "0.875rem" }),
  multiValue: (base) => ({ ...base, backgroundColor: ACCENT_SOFT, borderRadius: 6 }),
  multiValueLabel: (base) => ({ ...base, color: "#8a5a00", fontSize: "0.8rem" }),
  multiValueRemove: (base) => ({
    ...base,
    color: "#8a5a00",
    ":hover": { backgroundColor: ACCENT, color: "#fff" },
  }),
  option: (base, state) => ({
    ...base,
    fontSize: "0.875rem",
    backgroundColor: state.isSelected ? ACCENT : state.isFocused ? ACCENT_SOFT : "#fff",
    color: state.isSelected ? "#fff" : "inherit",
    ":active": { backgroundColor: ACCENT },
  }),
  indicatorSeparator: (base) => ({ ...base, backgroundColor: "rgba(0,0,0,0.15)" }),
  menu: (base) => ({ ...base, borderRadius: 8, overflow: "hidden", zIndex: 1500 }),
  menuPortal: (base) => ({ ...base, zIndex: 1500 }),
};

// Add this constant near the top of ReportFilterBar.tsx, after the selectStyles definition:

const FLAG_OPTIONS: SelectOption[] = [
  { value: "Rescheduled",  label: "Rescheduled"   },
  { value: "Delayed",      label: "Delayed"        },
  { value: "Part Shipment",label: "Part Shipment"  },
];
const ReportFilterBar: React.FC<Props> = ({
  mode,
  onModeChange,
  filters,
  setFilter,
  onGenerate,
  onReset,
  canGenerate,
  dateError,
  loading,
  customerOptions,
  carrierOptions,
}) => {
  const dateTypeOptions = getDateTypeOptions(mode);

  // shared sx for the outlined date fields (40px, amber focus, red on error)
  const dateFieldSx = (hasError = false) => ({
    minWidth: 160,
    "& .MuiOutlinedInput-root": {
      height: 40,
      borderRadius: 2,
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: hasError ? "error.main" : BORDER,
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: hasError ? "error.main" : "rgba(0,0,0,0.5)",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: hasError ? "error.main" : ACCENT,
        borderWidth: 1,
      },
    },
  });

  return (
    <Box
      sx={{
        p: 2.5,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: "background.paper",
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
      }}
    >
      <Stack direction="row" flexWrap="wrap" gap={2.5} alignItems="flex-start">
        {/* Mode (single select; swaps the Air/Ocean table in place) */}
        <Box>
          <FieldLabel required>Mode</FieldLabel>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={mode}
            onChange={(_e, val: ShipmentMode | null) => {
              if (val && val !== mode) onModeChange(val);
            }}
            sx={{
              "& .MuiToggleButton-root": {
                height: 40,
                px: 2,
                textTransform: "none",
                fontWeight: 600,
                borderColor: BORDER,
              },
              "& .Mui-selected": {
                bgcolor: `${ACCENT} !important`,
                color: "#fff !important",
                "&:hover": { bgcolor: `${ACCENT_DARK} !important` },
              },
            }}
          >
            <ToggleButton value="air">
              <Plane size={16} style={{ marginRight: 6 }} /> Air
            </ToggleButton>
            <ToggleButton value="ocean">
              <Ship size={16} style={{ marginRight: 6 }} /> Ocean
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>



        {/* Date range */}
        <Box>
          <FieldLabel required>Start Date</FieldLabel>
          <TextField
            size="small"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={filters.startDate}
            onChange={(e) => setFilter("startDate", e.target.value)}
            sx={dateFieldSx(false)}
          />
        </Box>

        <Box>
          <FieldLabel required>End Date</FieldLabel>
          <TextField
            size="small"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={filters.endDate}
            onChange={(e) => setFilter("endDate", e.target.value)}
            error={Boolean(dateError)}
            sx={dateFieldSx(Boolean(dateError))}
          />
        </Box>
        {/* Flags (optional, multi) */}
        <Box sx={{ minWidth: 220 }}>
          <FieldLabel>Flags</FieldLabel>
          <Select
            isMulti
            options={FLAG_OPTIONS}
            value={filters.flags}
            onChange={(val) => setFilter("flags", (val as SelectOption[]) ?? [])}
            placeholder="All flags"
            menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
            styles={selectStyles}
          />
        </Box>

        {/* Customer (optional, multi) */}
        <Box sx={{ minWidth: 240 }}>
          <FieldLabel>Customer</FieldLabel>
          <Select
            isMulti
            options={customerOptions}
            value={filters.customers}
            onChange={(val) => setFilter("customers", (val as SelectOption[]) ?? [])}
            placeholder="All customers"
            menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
            styles={selectStyles}
          />
        </Box>

        {/* Carrier (optional, multi; cleared whenever Mode changes) */}
        <Box sx={{ minWidth: 240 }}>
          <FieldLabel>Carrier</FieldLabel>
          <Select
            isMulti
            options={carrierOptions}
            value={filters.carriers}
            onChange={(val) => setFilter("carriers", (val as SelectOption[]) ?? [])}
            placeholder="All carriers"
            menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
            styles={selectStyles}
          />
        </Box>

        {/* Actions */}
        <Stack direction="row" gap={1} sx={{ alignSelf: "flex-end", pb: 0.25 }}>
          <Button
            variant="contained"
            disableElevation
            disabled={!canGenerate || loading}
            onClick={onGenerate}
            startIcon={<Play size={16} />}
            sx={{
              height: 40,
              px: 2.5,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              bgcolor: ACCENT,
              "&:hover": { bgcolor: ACCENT_DARK },
            }}
          >
            {loading ? "Generating…" : "Generate Report"}
          </Button>
          <Button
            variant="outlined"
            onClick={onReset}
            startIcon={<RotateCcw size={16} />}
            sx={{
              height: 40,
              px: 2,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              color: ACCENT,
              borderColor: ACCENT,
              "&:hover": { borderColor: ACCENT_DARK, bgcolor: ACCENT_SOFT },
            }}
          >
            Reset
          </Button>
        </Stack>
      </Stack>

      {dateError && (
        <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
          {dateError}
        </Alert>
      )}
    </Box>
  );
};

export default ReportFilterBar;